import styled from '@emotion/styled';
import { FormControl, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Typography } from '@material-ui/core';
import { FC, Fragment } from 'react';
import { ConversionGoal } from '../../../../../generated/graphql';
import { COLOR_TEXT_MAIN } from '../../../../../theme/colors';
import UrlMatcher from '../../../../../types/UrlMatcher';
import checkUrl from '../../../../../utility/checkUrl';
import StyledInputField from '../../../primitive/StyledInputField';
import InputError from '../InputError';
import StyledIconButton from '../StyledIconButton';
import StyledSelect from '../StyledSelect';
import CloseIcon from '@material-ui/icons/Close';
import ButtonGrid from '../ButtonGrid';
import StyledButton from '../StyledButton';

type Props = {
  goal: ConversionGoal;
  radioValue: string;
  handleRadioChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  urlMatchers: UrlMatcher[];
  setUrlMatchers: (value: React.SetStateAction<UrlMatcher[]>) => void;
  removeUrl: (index: number) => void;
  addExclusionUrl: () => void;
  addInclusionUrl: () => void;
};

const UrlContent: FC<Props> = ({
  goal,
  radioValue,
  handleRadioChange,
  urlMatchers,
  setUrlMatchers,
  removeUrl,
  addExclusionUrl,
  addInclusionUrl,
}) => {
  return (
    <Fragment>
      {goal.trigger.type === 'event' ? (
        <Fragment>
          <Grid item xs={12}>
            <Subtitle>イベントトラッキングを指定するページの対象デバイスとURLを設定してください</Subtitle>
          </Grid>
          <RadioGroupGrid item xs={12}>
            <FormControl component="fieldset">
              <StyledRadioGroup
                aria-label="device_type"
                name="device_type"
                value={radioValue}
                onChange={handleRadioChange}
              >
                <FormControlLabel value="all" control={<Radio />} label="すべて" />
                <FormControlLabel value="pc" control={<Radio />} label="PCのみ" />
                <FormControlLabel value="mobile" control={<Radio />} label="スマートフォンのみ" />
              </StyledRadioGroup>
            </FormControl>
          </RadioGroupGrid>
        </Fragment>
      ) : null}
      <Grid item xs={12}>
        <Subtitle>ゴールの対象とするページのURLを入れてください</Subtitle>
      </Grid>
      {urlMatchers.map((urlMatcher, index) => {
        if (!urlMatcher.isInclusion || urlMatcher.destroy) return;
        return (
          <UrlInputGrid key={`include-${urlMatcher.id}-${index}`} item xs={12}>
            <UrlInputWrapper>
              <UrlInputSelect
                value={urlMatcher.matchType}
                onChange={(e) => {
                  urlMatchers[index].matchType = e.target.value as unknown as MatchType;
                  setUrlMatchers(urlMatchers.slice());
                }}
                variant="outlined"
              >
                <MenuItem value={'simple'}>簡易一致</MenuItem>
                <MenuItem value={'regexp'}>正規表現</MenuItem>
                <MenuItem value={'exact'}>完全一致</MenuItem>
                <MenuItem value={'prefix'}>前方一致</MenuItem>
                <MenuItem value={'substring'}>部分一致</MenuItem>
              </UrlInputSelect>
              <FullWidthDiv>
                <StyledInputField
                  value={urlMatcher.expression}
                  placeholder={
                    urlMatcher.matchType === 'regexp' ? '正規表現を入力してください' : 'URLを入力してください'
                  }
                  onChange={(e) => {
                    urlMatchers[index].expression = e.target.value;
                    setUrlMatchers(urlMatchers.slice());
                  }}
                  error={
                    urlMatcher.expression.length > 2047 ||
                    (urlMatcher.matchType !== 'regexp' &&
                      urlMatcher.expression.length !== 0 &&
                      !checkUrl(urlMatcher.expression) &&
                      urlMatcher.matchType !== 'substring')
                  }
                  width="100%"
                />
                {urlMatcher.expression.length !== 0 &&
                  urlMatcher.matchType !== 'regexp' &&
                  !checkUrl(urlMatcher.expression) &&
                  urlMatcher.matchType !== 'substring' && (
                    <InputError>http:// または https:// で始まるURLを指定してください</InputError>
                  )}
                {urlMatcher.expression.length > 2047 && <InputError>2047文字以内で入力してください</InputError>}
              </FullWidthDiv>
              <StyledIconButton
                onClick={() => {
                  removeUrl(index);
                }}
              >
                <CloseIcon />
              </StyledIconButton>
            </UrlInputWrapper>
          </UrlInputGrid>
        );
      })}
      <ButtonGrid item xs={12}>
        <StyledButton variant="outlined" onClick={addInclusionUrl}>
          URLを追加
        </StyledButton>
      </ButtonGrid>
      <Grid item xs={12}>
        <Subtitle>上記の対象から除外するURLを入れてください</Subtitle>
      </Grid>
      {urlMatchers.map((urlMatcher, index) => {
        if (urlMatcher.isInclusion || urlMatcher.destroy) return;
        return (
          <UrlInputGrid key={`exclude-${urlMatcher.id}-${index}`} item xs={12}>
            <FlexWrapper>
              <UrlInputSelect
                value={urlMatcher.matchType}
                onChange={(e) => {
                  urlMatchers[index].matchType = e.target.value as unknown as MatchType;
                  setUrlMatchers(urlMatchers.slice());
                }}
                variant="outlined"
              >
                <MenuItem value={'simple'}>簡易一致</MenuItem>
                <MenuItem value={'regexp'}>正規表現</MenuItem>
                <MenuItem value={'exact'}>完全一致</MenuItem>
                <MenuItem value={'prefix'}>前方一致</MenuItem>
                <MenuItem value={'substring'}>部分一致</MenuItem>
              </UrlInputSelect>
              <FullWidthDiv>
                <StyledInputField
                  value={urlMatcher.expression}
                  placeholder={
                    urlMatcher.matchType === 'regexp' ? '正規表現を入力してください' : 'URLを入力してください'
                  }
                  onChange={(e) => {
                    urlMatchers[index].expression = e.target.value;
                    setUrlMatchers(urlMatchers.slice());
                  }}
                  error={
                    urlMatcher.expression.length > 2047 ||
                    (urlMatcher.matchType !== 'regexp' &&
                      urlMatcher.expression.length !== 0 &&
                      !checkUrl(urlMatcher.expression) &&
                      urlMatcher.matchType !== 'substring')
                  }
                  width="100%"
                />
                {urlMatcher.expression.length !== 0 &&
                  urlMatcher.matchType !== 'regexp' &&
                  !checkUrl(urlMatcher.expression) &&
                  urlMatcher.matchType !== 'substring' && (
                    <InputError>http:// または https:// で始まるURLを指定してください</InputError>
                  )}
                {urlMatcher.expression.length > 2047 && <InputError>2047文字以内で入力してください</InputError>}
              </FullWidthDiv>
              <StyledIconButton
                onClick={() => {
                  removeUrl(index);
                }}
              >
                <CloseIcon />
              </StyledIconButton>
            </FlexWrapper>
          </UrlInputGrid>
        );
      })}
      <ButtonGrid item xs={12}>
        <StyledButton variant="outlined" onClick={addExclusionUrl}>
          URLを追加
        </StyledButton>
      </ButtonGrid>
    </Fragment>
  );
};

const Subtitle = styled(Typography)`
  font-weight: 700;
  font-size: 18px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: ${COLOR_TEXT_MAIN};
`;

const RadioGroupGrid = styled(Grid)`
  margin: 12px 0px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
`;

const UrlInputGrid = styled(Grid)`
  margin-top: 16px;
`;

const UrlInputWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const UrlInputSelect = styled(StyledSelect)`
  margin-right: 16px;
`;

const FullWidthDiv = styled('div')`
  width: 100%;
`;

const FlexWrapper = styled('div')`
  display: flex;
`;

export default UrlContent;
